import { ModelText } from '@cadstrom/cadstrom-shared';
import { Utils } from '@cadstrom/sigma-engine-shared';

import type { DesignModels, ModelWithNetStats } from '@cadstrom/sigma-engine-shared';

export interface ModuleSearchMatch {
	projectModule: DesignModels.ProjectModule;
	moduleReferenceDesignator: string;
	schematicInstances: DesignModels.Module[];
}

export interface ModuleMetadataSummary {
	manufacturerPartNumber: string;
	manufacturer: string;
}

/**
 * Normalizes module search text by removing an optional command prefix and lowercasing the query.
 */
export function getModuleSearchTerm(search: string, prefix = '$') {
	return (search.startsWith(prefix) ? search.slice(prefix.length) : search).toLowerCase();
}

/**
 * Normalizes net search text by removing an optional command prefix and lowercasing the query.
 */
export function getNetSearchTerm(search: string, prefix = '#') {
	return (search.startsWith(prefix) ? search.slice(prefix.length) : search).toLowerCase();
}

/**
 * Returns module matches using the command bar's tuned project-module-first search behavior.
 */
export function getModuleSearchMatches(
	coreModel: ModelWithNetStats,
	searchTerm: string,
	options: { includeEmpty?: boolean; limit?: number } = {},
): ModuleSearchMatch[] {
	const limit = options.limit ?? 50;
	if (searchTerm === '' && options.includeEmpty !== true) {
		return [];
	}

	const projectModuleMatches = coreModel.modules
		.filter(
			(module) =>
				(searchTerm === '' || module.name.toLowerCase().includes(searchTerm)) &&
				(!coreModel.components[module.componentRef]?.isVirtual || module.name.toLowerCase() === searchTerm),
		)
		.map((projectModule) => ({
			projectModule,
			moduleReferenceDesignator: projectModule.name,
			schematicInstances: getSchematicModuleInstances(coreModel, projectModule),
		}));

	const matches =
		projectModuleMatches.length > 0 ? projectModuleMatches : getDesignModuleFallbackMatches(coreModel, searchTerm);

	return matches
		.sort((a, b) => {
			const aExact =
				a.moduleReferenceDesignator.toLowerCase() === searchTerm || a.projectModule.name.toLowerCase() === searchTerm;
			const bExact =
				b.moduleReferenceDesignator.toLowerCase() === searchTerm || b.projectModule.name.toLowerCase() === searchTerm;
			if (aExact !== bExact) {
				return aExact ? -1 : 1;
			}

			return Utils.naturalStringSort(a.moduleReferenceDesignator, b.moduleReferenceDesignator);
		})
		.slice(0, limit);
}

/**
 * Returns net-name matches using the command bar's search behavior, including case-insensitive local-name matching.
 */
export function getNetSearchMatches(
	coreModel: ModelWithNetStats,
	searchTerm: string,
	options: { includeEmpty?: boolean; limit?: number } = {},
) {
	const limit = options.limit ?? 50;
	if (searchTerm === '' && options.includeEmpty !== true) {
		return [];
	}

	return Array.from(
		new Set(
			Object.values(coreModel.designs)
				.flatMap(({ nets }) => Object.entries(nets))
				.filter(([netName, net]) => {
					if (searchTerm === '') {
						return true;
					}

					const dialects = [ModelText.getTextDialectForCadTool(coreModel.type), ModelText.TextDialect.Cadstrom];
					return [netName, net.localName, ...(net.localNames ?? [])]
						.filter((name): name is string => name !== undefined)
						.some((name) =>
							ModelText.searchHaystack({ raw: name, dialects }).some((spelling) =>
								spelling.toLowerCase().includes(searchTerm),
							),
						);
				})
				.map(([netName]) => netName),
		),
	)
		.slice(0, limit)
		.sort((a, b) => Utils.naturalStringSort(a, b));
}

/**
 * Extracts module metadata fields used by command bar and markdown suggestion displays.
 */
export function getModuleMetadataSummary(module: DesignModels.ProjectModule): ModuleMetadataSummary {
	const manufacturerPartNumber = getStringMetadataValue(module.metadata?.['MANUFACTURER PART #'] ?? module.value) ?? '';
	const manufacturer = getStringMetadataValue(module.metadata?.['MANUFACTURER']) ?? '';
	return { manufacturerPartNumber, manufacturer };
}

/**
 * Formats module metadata as compact secondary suggestion text.
 */
export function getModuleMetadataDetail(module: DesignModels.ProjectModule) {
	const { manufacturerPartNumber, manufacturer } = getModuleMetadataSummary(module);
	return [manufacturerPartNumber, manufacturer].filter((value) => value !== '').join(' | ') || undefined;
}

/**
 * Returns schematic module instances for a project module.
 */
export function getSchematicModuleInstances(coreModel: ModelWithNetStats, projectModule: DesignModels.ProjectModule) {
	return projectModule.instances
		.filter((instance) => coreModel.designs[instance.designId]?.modelType === 'schematic')
		.map((instance) => {
			const design = coreModel.designs[instance.designId];
			return design?.modules.find((module) => module.ref === instance.ref);
		})
		.filter((module): module is NonNullable<typeof module> => module !== undefined);
}

/**
 * Finds design-level module matches when project-level module names do not match.
 */
function getDesignModuleFallbackMatches(coreModel: ModelWithNetStats, searchTerm: string): ModuleSearchMatch[] {
	const referenceDesignators = new Set<string>();
	const matches: ModuleSearchMatch[] = [];

	for (const design of Object.values(coreModel.designs)) {
		for (const designModule of design.modules) {
			if (referenceDesignators.has(designModule.name)) {
				continue;
			}

			if (
				coreModel.components[designModule.componentRef]?.isVirtual &&
				designModule.name.toLowerCase() !== searchTerm
			) {
				continue;
			}

			if (
				!designModule.name.toLowerCase().includes(searchTerm) &&
				!designModule.fullModuleName?.toLowerCase().includes(searchTerm)
			) {
				continue;
			}

			referenceDesignators.add(designModule.name);
			const projectModule = coreModel.modules.find((module) => module.name === designModule.fullModuleName);
			if (projectModule !== undefined) {
				matches.push({
					projectModule,
					moduleReferenceDesignator: designModule.name,
					schematicInstances: getSchematicModuleInstances(coreModel, projectModule),
				});
			}
		}
	}

	return matches;
}

/**
 * Normalizes string-like metadata values into displayable suggestion text.
 */
function getStringMetadataValue(value: unknown) {
	return typeof value === 'string' || typeof value === 'number' ? String(value) : undefined;
}
